import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import useDocumentMeta from "../../hooks/useDocumentMeta";
import { slugify, formatPostDate } from "../../utils/slug";
import "./PostPage.css";

// Zamienia adresy stron pojawiające się w treści wpisu (http://, https://,
// www.) na klikalne linki otwierane w nowej karcie.
const URL_PATTERN = /((?:https?:\/\/|www\.)[^\s<]+)/gi;

function linkifyLine(text, keyPrefix) {
  const parts = text.split(URL_PATTERN);
  return parts.map((part, i) => {
    if (!/^(?:https?:\/\/|www\.)/i.test(part)) {
      return part;
    }
    // Nie łapiemy interpunkcji na końcu zdania (kropka, przecinek itp.) do linku.
    const trailingMatch = part.match(/^(.*?)([.,;:!?)\]]*)$/);
    const core = trailingMatch ? trailingMatch[1] : part;
    const trailing = trailingMatch ? trailingMatch[2] : "";
    const href = /^https?:\/\//i.test(core) ? core : `https://${core}`;
    return (
      <React.Fragment key={`${keyPrefix}-${i}`}>
        <a href={href} target="_blank" rel="noopener noreferrer" className="post-page-link">
          {core}
        </a>
        {trailing}
      </React.Fragment>
    );
  });
}

// Przyciski udostępniania: Facebook ma oficjalny, działający link do
// udostępniania. Instagram niestety nie udostępnia żadnego publicznego
// mechanizmu "udostępnij post" ze zwykłej strony internetowej — jedyne co
// realnie działa to link "Udostępnij" (menu systemowe telefonu, gdzie wśród
// aplikacji może pojawić się Instagram) oraz kopiowanie linku do wklejenia
// ręcznie (np. w Instagram Stories czy wiadomości).
const ShareButtons = ({ url, title }) => {
  const [copied, setCopied] = useState(false);
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  const handleNativeShare = () => {
    if (canNativeShare) {
      navigator.share({ title, url }).catch(() => {});
    }
  };

  const handleCopy = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {});
    }
  };

  return (
    <div className="post-page-share">
      <span className="post-page-share-label">Udostępnij:</span>
      <a
        className="post-page-share-btn post-page-share-fb"
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Facebook
      </a>
      {canNativeShare && (
        <button type="button" className="post-page-share-btn" onClick={handleNativeShare}>
          Udostępnij (Instagram i inne)
        </button>
      )}
      <button type="button" className="post-page-share-btn" onClick={handleCopy}>
        {copied ? "Skopiowano!" : "Kopiuj link"}
      </button>
    </div>
  );
};

const PostPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(undefined); // undefined = ładowanie, null = nie znaleziono
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("https://ekselowe.pl/api/getposts.php", {
      headers: { "Content-Type": "application/json; charset=UTF-8" },
    })
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        const keys = Object.keys(json || {});
        // Adres podstrony to nazwa wpisu (slug), nie numer — ale stare linki
        // z numerem w adresie nadal działają jako zapasowe dopasowanie.
        let key = keys.find((k) => slugify(json[k].title) === postId);
        if (key === undefined && keys.includes(postId)) {
          key = postId;
        }
        if (key === undefined) {
          setNotFound(true);
        } else {
          setPost(json[key]);
        }
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [postId]);

  const description = post && post.text ? post.text.slice(0, 155) : undefined;

  useDocumentMeta({
    title: post ? post.title : "Aktualności",
    description,
    image: post && post.image,
    url: typeof window !== "undefined" ? window.location.href : undefined,
    keywords: post && post.keywords,
  });

  if (notFound) {
    return (
      <div className="post-page-container">
        <div className="post-page-content">
          <p className="post-page-back-link" onClick={() => navigate("/aktualnosci")}>&larr; Wróć do Aktualności</p>
          <h1>Nie znaleziono wpisu</h1>
          <p>Ten wpis mógł zostać usunięty lub link jest nieprawidłowy.</p>
          <Link to="/aktualnosci" className="post-page-cta">Zobacz wszystkie wpisy</Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="post-page-container">
        <div className="post-page-content">
          <p>Wczytywanie…</p>
        </div>
      </div>
    );
  }

  const paragraphs = post.text.split("\n").filter((line) => line.trim() !== "");
  const galleryImages = Array.isArray(post.images) ? post.images.filter(Boolean) : [];

  return (
    <div className="post-page-container">
      <div className="post-page-content">
        <p className="post-page-back-link" onClick={() => navigate("/aktualnosci")}>&larr; Wróć do Aktualności</p>
        {post.image && (
          <div className="post-page-image-frame">
            <img src={post.image} alt={post.title} className="post-page-image" />
          </div>
        )}
        <h1 className="post-page-title">{post.title}</h1>
        {post.date && <p className="post-page-date">{formatPostDate(post.date)}</p>}
        <ShareButtons
          url={typeof window !== "undefined" ? window.location.href : ""}
          title={post.title}
        />
        <div className="post-page-text">
          {paragraphs.map((line, i) => (
            <p key={`p${i}`}>{linkifyLine(line, `p${i}`)}</p>
          ))}
        </div>
        {galleryImages.length > 0 && (
          <div className="post-page-gallery">
            {galleryImages.map((src, i) => (
              <img key={`img${i}`} src={src} alt="" className="post-page-gallery-img" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PostPage;
